<template>
    <div class="relative group">
        <button
            v-show="showFadeLeft"
            type="button"
            @click="scroll('left')"
            aria-label="Scroll left"
            class="hidden lg:group-hover:flex absolute left-2 top-1/2 -translate-y-1/2 z-20 w-9 h-9 items-center justify-center !rounded-full bg-white shadow-lg hover:bg-gray-50 transition"
        >
            <span class="pi pi-chevron-left text-primary text-sm" />
        </button>

        <div
            ref="scroller"
            @scroll="updateFade"
            class="flex items-stretch gap-3 overflow-x-auto scroll-smooth py-2 px-1 snap-x"
            style="scrollbar-width: none"
        >
            <slot />
        </div>

        <button
            v-show="showFadeRight"
            type="button"
            @click="scroll('right')"
            aria-label="Scroll right"
            class="hidden lg:group-hover:flex absolute right-2 top-1/2 -translate-y-1/2 z-20 w-9 h-9 items-center justify-center !rounded-full bg-white shadow-lg hover:bg-gray-50 transition"
        >
            <span class="pi pi-chevron-right text-primary text-sm" />
        </button>

        <div
            v-show="canScroll"
            class="lg:hidden mx-auto mt-1 h-1 w-16 rounded-full bg-gray-200 overflow-hidden"
            aria-hidden="true"
        >
            <div
                class="h-full bg-primary rounded-full"
                :style="{
                    width: thumbWidth + '%',
                    marginLeft: thumbLeft + '%',
                }"
            />
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, nextTick } from 'vue'

const scroller = ref(null)
const showFadeLeft = ref(false)
const showFadeRight = ref(false)
const canScroll = ref(false)
const thumbWidth = ref(100)
const thumbLeft = ref(0)

function updateFade(e) {
    const el = e?.target ?? scroller.value
    if (!el) return
    showFadeLeft.value = el.scrollLeft > 0
    showFadeRight.value = el.scrollLeft + el.clientWidth < el.scrollWidth - 1

    const overflow = el.scrollWidth - el.clientWidth
    canScroll.value = overflow > 1
    if (canScroll.value) {
        thumbWidth.value = (el.clientWidth / el.scrollWidth) * 100
        thumbLeft.value = (el.scrollLeft / el.scrollWidth) * 100
    } else {
        thumbWidth.value = 100
        thumbLeft.value = 0
    }
}

function scroll(direction) {
    const el = scroller.value
    if (!el) return
    el.scrollBy({
        left: direction === 'left' ? -el.clientWidth * 0.8 : el.clientWidth * 0.8,
        behavior: 'smooth',
    })
}

let resizeObserver = null

onMounted(async () => {
    await nextTick()
    updateFade()

    if (scroller.value && 'ResizeObserver' in window) {
        resizeObserver = new ResizeObserver(() => updateFade())
        resizeObserver.observe(scroller.value)
    }
})

onBeforeUnmount(() => {
    resizeObserver?.disconnect()
    resizeObserver = null
})
</script>

<style scoped>
div[ref="scroller"]::-webkit-scrollbar,
.overflow-x-auto::-webkit-scrollbar {
    display: none;
}
</style>
